import { useEffect, useState } from "react";

import { Button, Callout, FormGroup, Icon } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { isComponentInProgressOrOverdue } from "@/components/pages_logged_in/program/components/componentMetadata";
import { decodeHtmlEntities } from "@/components/pages_logged_in/roleplays/details/scenario/Notes";
import { FormField } from "@/components/pieces/coaching/AdditionalInfoForm";
import { PrettyCheckbox } from "@/components/pieces/form/PrettyCheckbox";
import { Truncate } from "@/components/pieces/Truncate";
import { MIDTEXT_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import { ComponentStatus, FormFieldAnswerType } from "@/graphql";
import { ParticipantComponent } from "@/types/programs";
import { SurveyAnswer } from "@/types/surveys";
import { NonNullableArrayItem } from "@/utils/types";

export const ActionItemsFormFieldHelpText: Partial<
  Record<FormFieldAnswerType, string>
> = {
  [FormFieldAnswerType.Task]: "",
  [FormFieldAnswerType.SingleLine]:
    "Participants will be asked to write out their answer",
  [FormFieldAnswerType.MultiLine]:
    "Participants will be asked to write out their answer",
  [FormFieldAnswerType.Url]: "Participants will be asked to provide a URL",
  [FormFieldAnswerType.File]: "Participants will be asked to upload a file",
};

const getHelpText = (answerType: FormFieldAnswerType) => {
  if (!answerType || !ActionItemsFormFieldHelpText[answerType]) return "";
  return ActionItemsFormFieldHelpText[answerType];
};

interface ActionItemAnswerProps {
  answer: SurveyAnswer;
  answerType: FormFieldAnswerType;
}

export function ActionItemAnswer({
  answer,
  answerType,
}: ActionItemAnswerProps) {
  const { value } = answer;
  if (answerType === FormFieldAnswerType.Url) {
    let urlInfo = {
      text: "",
      url: "",
    };

    if (value) {
      try {
        urlInfo = JSON.parse(value);
      } catch (error) {
        console.log("Error parsing url", error);
      }
    }

    return (
      <Flex mt={2} alignItems="center">
        <Icon icon="link" />
        <Box ml={2}>
          <a
            href={urlInfo?.url}
            target="_blank"
            style={{
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {urlInfo?.text}
          </a>
        </Box>
      </Flex>
    );
  }
  if (answerType === FormFieldAnswerType.File) {
    return (
      <Flex alignItems="center" mb={2}>
        <Icon icon="paperclip" color={SUBTEXT_COLOR} />
        <Box ml={1}>
          <a href={answer?.userFile?.url} target="_BLANK">
            <Truncate>{answer?.userFile?.name}</Truncate>
          </a>
        </Box>
      </Flex>
    );
  }
  if (answerType === FormFieldAnswerType.Task) {
    return null;
  }
  return <Box mt={2}>{decodeHtmlEntities(value || "")}</Box>;
}

const getUrlAnswerValue = (answer: SurveyAnswer) => {
  const { value } = answer;

  let urlInfo = {
    text: "",
    url: "",
  };

  if (!value) return urlInfo;

  try {
    urlInfo = JSON.parse(value);
  } catch (error) {
    console.log("Error parsing url", error);
  }
  return urlInfo;
};

const getFileAnswerValue = (answer: SurveyAnswer) => {
  return answer?.userFile?.id;
};

const getAnswerValue = (
  answer: SurveyAnswer,
  answerType: FormFieldAnswerType,
) => {
  if (answerType === FormFieldAnswerType.Url) {
    return getUrlAnswerValue(answer);
  }
  if (answerType === FormFieldAnswerType.File) {
    return getFileAnswerValue(answer);
  }
  return answer?.value;
};

type FormFieldType = NonNullableArrayItem<
  NonNullable<ParticipantComponent["survey"]>["formFields"]
>;

interface ActionItemDisplayProps {
  question: FormFieldType;
  answer?: SurveyAnswer;
  isComplete: boolean;
  isLead: boolean;
  onSubmitAnswer?: (value: string) => void;
  componentStatus?: ComponentStatus;
  readOnly?: boolean;
  isFinalQuestion?: boolean;
  hasLinkIcon?: boolean;
  isVideoOrArticle?: boolean;
}

export function ActionItemDisplay({
  question,
  answer,
  isComplete,
  isLead,
  onSubmitAnswer,
  componentStatus,
  readOnly = false,
  isFinalQuestion = false,
  hasLinkIcon = true,
  isVideoOrArticle = false,
}: ActionItemDisplayProps) {
  const { prompt, link, answerType } = question;
  const { url, text } = link || {};
  const [hasData, setHasData] = useState(false);
  const isTaskAnswer = answerType === FormFieldAnswerType.Task;
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isChecked, setIsChecked] = useState(isComplete);
  const isCheckable =
    !isLead && isComponentInProgressOrOverdue(componentStatus) && !readOnly;

  useEffect(() => {
    setIsChecked(isComplete);
  }, [isComplete]);

  const submitValueHandler = (value: string) => {
    if (!value) {
      setError("Please provide an answer");

      // reset isChecked if answer is empty, because of optimistic update
      setIsChecked(false);
      setIsSubmitting(false);

      return;
    }

    const initValue = answer ? getAnswerValue(answer, answerType) : "";
    if (value !== initValue) {
      onSubmitAnswer?.(value.toString());
    }

    setIsSubmitting(false);
  };

  const handleToggle = () => {
    const _checked = !isChecked;
    setIsSubmitting(false);

    // allow optimistic update if not final question
    if (!isFinalQuestion) {
      setIsChecked(_checked);
    }

    // task is special case, no need to submit value, checked value is enough
    if (isTaskAnswer) {
      onSubmitAnswer?.(_checked ? `${_checked}` : "");
    } else if (_checked) {
      setIsSubmitting(true);
    }
  };

  return (
    <Flex alignItems="start" style={{ flex: 1 }}>
      <Box mr={3}>
        {!isVideoOrArticle && (
          <PrettyCheckbox
            size={24}
            onChange={() => {
              handleToggle();
              // task doesn't need to submit answer
            }}
            checked={isChecked}
            disabled={!isCheckable}
          />
        )}
      </Box>
      <Box flex={1}>
        {!isVideoOrArticle && (
          <Box
            onClick={() => {
              if (isCheckable) {
                handleToggle();
              }
            }}
            css={{
              textDecoration: isChecked ? "line-through" : "none",
              cursor: !isCheckable ? "default" : "pointer",
            }}
          >
            {prompt}
          </Box>
        )}
        {link && (
          <Flex mt={isVideoOrArticle ? 0 : 2} alignItems={"center"}>
            {hasLinkIcon && <Icon icon="link" />}
            <Box
              ml={hasLinkIcon ? 1 : 0}
              css={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <a
                style={{
                  color: MIDTEXT_COLOR,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
                href={url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {text}
              </a>
            </Box>
          </Flex>
        )}
        {isLead && (
          <Box mt={2} color={SUBTEXT_COLOR} fontStyle="italic">
            {getHelpText(answerType)}
          </Box>
        )}
        {isComponentInProgressOrOverdue(componentStatus) &&
          !readOnly &&
          !isLead && (
            <Box mt={2}>
              <FormGroup
                key={question.id}
                label={null}
                labelFor={`field-${question.id}`}
                labelInfo={null}
                helperText={null}
                style={{ marginBottom: 0 }}
              >
                <FormField
                  disabled={isChecked || isSubmitting}
                  userFile={answer?.userFile ?? undefined}
                  initValue={answer?.value || answer?.userFile?.id}
                  formField={question}
                  submitting={isSubmitting}
                  onSubmitReady={(value) => {
                    submitValueHandler(value);
                  }}
                  onSubmitError={() => {
                    setIsChecked(false);
                    setIsSubmitting(false);
                  }}
                  onValueChange={(data) => {
                    setHasData(data.hasValue);
                    setError("");
                    setIsChecked(false);
                  }}
                  placeholder={"Write your answer here"}
                />
                {error && (
                  <Box mt={2}>
                    <Callout intent="danger" icon="warning-sign">
                      {error}
                    </Callout>
                  </Box>
                )}
                {hasData && !isTaskAnswer && !isChecked && (
                  <Box mt={2}>
                    <Button
                      style={{ minWidth: 135 }}
                      intent="primary"
                      onClick={() => {
                        handleToggle();
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </FormGroup>
            </Box>
          )}
        {answer && (componentStatus !== "Ongoing" || readOnly) && (
          <ActionItemAnswer answer={answer} answerType={question?.answerType} />
        )}
      </Box>
    </Flex>
  );
}
